import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { TrendUp, Trophy, Flag, Bug, Sparkle, CaretDown, CaretRight, Eye, EyeSlash, Plus, Fire, CheckCircle, Terminal, CalendarBlank, ArrowRight, WarningCircle, Coins } from "@phosphor-icons/react";
import { LiveActivityTicker } from "./LivePulseStrip";
import { useScrollColumn } from "../lib/useScrollColumn";

const PAGE_SIZE = 3;             // initial "preview" rows per widget — keeps the rail compact
const LOAD_MORE_STEP = 5;        // how many more rows we add per "Load more" click

const COLLAPSE_KEY = "hc_rail_collapsed";
const HIDE_KEY = "hc_rail_hidden";

function readSet(key) {
    try { return new Set(JSON.parse(localStorage.getItem(key) || "[]")); } catch { return new Set(); }
}
function writeSet(key, set) {
    try { localStorage.setItem(key, JSON.stringify(Array.from(set))); } catch { /* private mode */ }
}

/**
 * HomeRightRail — info-dense right column. Each widget can be collapsed (— button),
 * hidden entirely (eye button), or expanded one page at a time (Load more). State
 * persists across reloads via localStorage so the rail remembers each user's
 * preferences without server round-trips.
 */
export default function HomeRightRail() {
    const [collapsed, setCollapsed] = useState(() => readSet(COLLAPSE_KEY));
    const [hidden, setHidden] = useState(() => readSet(HIDE_KEY));

    const toggleCollapsed = useCallback((id) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            writeSet(COLLAPSE_KEY, next);
            return next;
        });
    }, []);
    const toggleHidden = useCallback((id) => {
        setHidden((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            writeSet(HIDE_KEY, next);
            return next;
        });
    }, []);

    const ctx = { collapsed, hidden, toggleCollapsed, toggleHidden };

    const hiddenCount = hidden.size;
    const scrollRef = useScrollColumn("right-rail");

    return (
        <aside
            ref={scrollRef}
            className="col-fade hidden xl:block w-[300px] 2xl:w-[320px] shrink-0 space-y-3 h-full overflow-y-auto scrollbar-hidden py-6 sm:py-8 pl-2"
            data-testid="home-right-rail"
        >
            <DailyOpsCard ctx={ctx} />
            <LiveActivityTicker />
            <AttackBoxMiniCard ctx={ctx} />
            <CryptPeTipCard ctx={ctx} />
            <TrendingTagsCard ctx={ctx} />
            <TopHackersCard ctx={ctx} />
            <ActiveCtfsCard ctx={ctx} />
            <UpcomingEventsCard ctx={ctx} />
            <OpenBountiesCard ctx={ctx} />
            <ShortcutCard ctx={ctx} />

            {hiddenCount > 0 && (
                <button
                    onClick={() => { setHidden(new Set()); writeSet(HIDE_KEY, new Set()); }}
                    className="w-full text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-cyan-300 border border-dashed border-white/10 hover:border-cyan-500/40 px-3 py-2 rounded-sm transition-colors"
                    data-testid="rail-restore-hidden"
                >
                    + restore {hiddenCount} hidden widget{hiddenCount === 1 ? "" : "s"}
                </button>
            )}
        </aside>
    );
}


/**
 * CardShell — wraps a widget. The header has an icon + title + an action slot
 * + a [—] collapse toggle + an [eye] hide toggle. Collapsed cards still
 * show the header so the user can restore them in one click.
 */
function CardShell({ id, icon: Icon, title, kicker, action, testid, ctx, children, footer }) {
    if (ctx.hidden.has(id)) return null;
    const isCollapsed = ctx.collapsed.has(id);
    return (
        <section className="hc-surface" data-testid={testid}>
            <header className="px-4 py-3 flex items-center gap-2">
                <button
                    onClick={() => ctx.toggleCollapsed(id)}
                    className="flex items-center gap-2 min-w-0 flex-1 text-left text-slate-200 hover:text-white transition-colors"
                    title={isCollapsed ? "Expand" : "Collapse"}
                    data-testid={`${testid}-collapse-btn`}
                >
                    {isCollapsed ? <CaretRight size={10} weight="bold" className="shrink-0 text-slate-500" /> : <CaretDown size={10} weight="bold" className="shrink-0 text-slate-500" />}
                    <Icon size={14} weight="duotone" className="text-cyan-400 shrink-0" />
                    <h3 className="text-[13px] font-semibold tracking-tight truncate" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>{title}</h3>
                </button>
                {!isCollapsed && action}
                <button
                    onClick={() => ctx.toggleHidden(id)}
                    title="Hide this widget"
                    data-testid={`${testid}-hide-btn`}
                    className="text-slate-600 hover:text-rose-300 shrink-0 transition-colors"
                >
                    <EyeSlash size={11} weight="bold" />
                </button>
            </header>
            {!isCollapsed && (
                <>
                    <div className="px-4 pb-3">
                        {kicker && <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2.5">{kicker}</div>}
                        {children}
                    </div>
                    {footer}
                </>
            )}
        </section>
    );
}


function CardSkeleton({ rows = 3 }) {
    return (
        <ul className="space-y-1.5">
            {Array.from({ length: rows }).map((_, i) => (
                <li key={i} className="h-4 bg-slate-800/60 rounded-sm animate-pulse" />
            ))}
        </ul>
    );
}


/**
 * useWidgetData — generic pagination hook. Fetches initial PAGE_SIZE items and
 * lets the caller `loadMore()` to fetch LOAD_MORE_STEP more on demand.
 * `extract` lets the caller normalise the API response shape.
 */
function useWidgetData(buildUrl, extract = (d) => d) {
    const [items, setItems] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        api.get(buildUrl(0, PAGE_SIZE))
            .then(({ data }) => {
                const arr = extract(data);
                setItems(arr);
                setHasMore(arr.length === PAGE_SIZE);
            })
            .catch(() => { setItems([]); setHasMore(false); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadMore = useCallback(async () => {
        if (!items || loadingMore) return;
        setLoadingMore(true);
        try {
            const { data } = await api.get(buildUrl(items.length, LOAD_MORE_STEP));
            const arr = extract(data);
            setItems((prev) => [...(prev || []), ...arr]);
            setHasMore(arr.length === LOAD_MORE_STEP);
        } catch { setHasMore(false); }
        setLoadingMore(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, loadingMore]);

    return { items, hasMore, loadingMore, loadMore };
}


function LoadMoreFooter({ hasMore, loadingMore, onClick, testid }) {
    if (!hasMore) return null;
    return (
        <button
            onClick={onClick}
            disabled={loadingMore}
            data-testid={testid}
            className="w-full text-center text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-cyan-300 py-2.5 border-t border-white/5 disabled:opacity-50 flex items-center justify-center gap-1.5 transition-colors"
        >
            <Plus size={10} weight="bold" /> {loadingMore ? "loading…" : "load more"}
        </button>
    );
}


function DailyOpsCard({ ctx }) {
    const [streak, setStreak] = useState(null);
    const [quests, setQuests] = useState(null);
    useEffect(() => {
        api.get("/me/streak").then(({ data }) => setStreak(data)).catch(() => setStreak({}));
        api.get("/me/quests").then(({ data }) => setQuests(Array.isArray(data) ? data : [])).catch(() => setQuests([]));
    }, []);
    const dailies = quests ? quests.filter((q) => q.period === "daily").slice(0, 3) : null;
    const saved = streak?.last_active === new Date().toISOString().slice(0, 10);
    const days = streak?.current_streak || 0;
    return (
        <CardShell id="daily" icon={Fire} title="Daily ops" kicker="your day at a glance" testid="rail-daily" ctx={ctx}
            action={<Link to="/quests" className="text-[10px] font-mono text-slate-500 hover:text-cyan-300 uppercase tracking-widest">all</Link>}
        >
            {streak === null || dailies === null ? <CardSkeleton /> : (
                <div className="space-y-2.5">
                    <div className={`px-2.5 py-2 rounded-sm border ${saved ? "border-emerald-500/30 bg-emerald-500/[0.06]" : "border-amber-500/30 bg-amber-500/[0.06]"}`} data-testid="rail-streak">
                        <span className={`flex items-center gap-1.5 text-[12px] font-mono ${saved ? "text-emerald-300" : "text-amber-300"}`}>
                            <Fire size={14} weight="fill" /> {days}-day streak
                        </span>
                        <div className={`mt-1 text-[9px] uppercase tracking-widest font-mono ${saved ? "text-emerald-400/80" : "text-amber-400/80"}`}>
                            {saved ? "saved today — keep it burning" : "unsaved — solve anything by 00:00 utc"}
                        </div>
                    </div>
                    <ul className="space-y-1.5">
                        {dailies.length === 0 ? (
                            <li className="text-[11px] font-mono text-slate-500">// no daily quests</li>
                        ) : dailies.map((q) => (
                            <li key={q.id} className="flex items-center gap-2 text-[12px]" data-testid={`rail-quest-${q.id}`}>
                                {q.completed
                                    ? <CheckCircle size={13} weight="fill" className="text-emerald-400 shrink-0" />
                                    : <span className="w-[13px] h-[13px] rounded-full border border-slate-600 shrink-0" />}
                                <Link to="/quests" className={`truncate flex-1 min-w-0 hover:text-cyan-300 ${q.completed ? "text-slate-500 line-through" : "text-slate-200"}`}>
                                    {q.title}
                                </Link>
                                <span className="text-[10px] font-mono text-slate-500 shrink-0">
                                    {q.completed ? `+${q.reward}` : `${q.progress}/${q.target} · +${q.reward}xp`}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </CardShell>
    );
}


function CryptPeTipCard({ ctx }) {
    return (
        <CardShell id="cryptpe" icon={Coins} title="Get paid in crypto" kicker="for freelance hackers" testid="rail-cryptpe" ctx={ctx}
            action={<a href="https://crypt.pe" target="_blank" rel="noopener noreferrer" className="text-[10px] font-mono text-slate-500 hover:text-cyan-300 uppercase tracking-widest">crypt.pe</a>}
        >
            <div className="space-y-2.5">
                <p className="text-[12px] text-slate-400 leading-relaxed">
                    Doing paid work? Skip juggling QR codes and raw wallet addresses — send clients{" "}
                    <span className="text-cyan-300">one link</span>, get paid to your own wallet at{" "}
                    <span className="text-cyan-300">0% platform fee</span>.
                </p>
                <a
                    href="https://crypt.pe"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="rail-cryptpe-cta"
                    className="group/cta w-full inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-mono rounded-sm px-3 py-2 border border-white/15 text-cyan-300 hover:border-cyan-500/40 hover:bg-cyan-500/10 transition-colors"
                >
                    accept crypto payments
                    <ArrowRight size={11} className="transition-transform group-hover/cta:translate-x-0.5" />
                </a>
            </div>
        </CardShell>
    );
}


function AttackBoxMiniCard({ ctx }) {
    const [bal, setBal] = useState(null);
    useEffect(() => {
        api.get("/box/balance").then(({ data }) => setBal(data)).catch(() => setBal({}));
    }, []);
    const total = bal?.total ?? 0;
    const totalWin = bal?.total_windows ?? 0;
    const active = !!bal?.active_session;
    const hasHours = total > 0 || totalWin > 0;
    const lowHours = hasHours && !active && total < 1 && totalWin < 1;
    const HF = "'Space Grotesk', 'Chivo', sans-serif";
    return (
        <CardShell id="box" icon={Terminal} title="Attack Box" kicker="cloud kali in your browser" testid="rail-box" ctx={ctx}
            action={<Link to="/box" className="text-[10px] font-mono text-slate-500 hover:text-cyan-300 uppercase tracking-widest">open</Link>}
        >
            {bal === null ? <CardSkeleton rows={2} /> : (
                <div className="space-y-2.5">
                    {/* Hours readout — split by flavor, HUD data-table look */}
                    <div className="relative grid grid-cols-2 border border-white/10 rounded-sm overflow-hidden divide-x divide-white/10 bg-[#080c15]">
                        <div className="pointer-events-none absolute -top-8 -left-6 w-28 h-28 rounded-full" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.12), transparent 70%)" }} />
                        <div className="relative p-2.5">
                            <div className="text-[8px] uppercase tracking-[0.2em] font-mono text-cyan-400/80 mb-1">Kali</div>
                            <div className="text-xl font-black tracking-tight leading-none text-slate-100" style={{ fontFamily: HF }} data-testid="rail-box-hours">
                                {total}<span className="text-[11px] text-slate-500 font-bold ml-0.5">h</span>
                            </div>
                        </div>
                        <div className="relative p-2.5">
                            <div className="text-[8px] uppercase tracking-[0.2em] font-mono text-sky-400/80 mb-1">Windows</div>
                            <div className={`text-xl font-black tracking-tight leading-none ${totalWin > 0 ? "text-slate-100" : "text-slate-600"}`} style={{ fontFamily: HF }} data-testid="rail-box-hours-windows">
                                {totalWin}<span className="text-[11px] text-slate-500 font-bold ml-0.5">h</span>
                            </div>
                        </div>
                    </div>

                    {active && (
                        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest font-mono text-emerald-300" data-testid="rail-box-active">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> session running
                        </div>
                    )}

                    {lowHours && (
                        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest font-mono text-amber-400/90" data-testid="rail-box-low">
                            <WarningCircle size={12} weight="fill" className="text-amber-400 shrink-0" /> running low — top up
                        </div>
                    )}

                    <Link
                        to="/box"
                        data-testid="rail-box-cta"
                        className={`group/cta w-full inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-mono rounded-sm px-3 py-2 border transition-colors ${active
                            ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                            : lowHours
                                ? "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                                : "border-white/15 text-cyan-300 hover:border-cyan-500/40 hover:bg-cyan-500/10"}`}
                    >
                        {active ? "resume session" : lowHours ? "top up hours" : hasHours ? "launch a box" : "get hours · from $4.99"}
                        <ArrowRight size={11} className="transition-transform group-hover/cta:translate-x-0.5" />
                    </Link>
                </div>
            )}
        </CardShell>
    );
}


function UpcomingEventsCard({ ctx }) {
    const [all, setAll] = useState(null);
    const [shown, setShown] = useState(PAGE_SIZE);
    useEffect(() => {
        api.get("/directory/events/external?limit=12")
            .then(({ data }) => {
                const items = Array.isArray(data) ? data : (data?.items || []);
                const now = Date.now();
                setAll(items.filter((e) => !e.finish || new Date(e.finish).getTime() > now));
            })
            .catch(() => setAll([]));
    }, []);
    const visible = all ? all.slice(0, shown) : null;
    const hasMore = all ? shown < all.length : false;
    const fmt = (iso) => { const d = new Date(iso); return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" }); };
    return (
        <CardShell id="events" icon={CalendarBlank} title="Upcoming CTFs" kicker="from ctftime & friends" testid="rail-events" ctx={ctx}
            action={<Link to="/events" className="text-[10px] font-mono text-slate-500 hover:text-cyan-300 uppercase tracking-widest">all</Link>}
            footer={<LoadMoreFooter hasMore={hasMore} loadingMore={false} onClick={() => setShown((s) => s + LOAD_MORE_STEP)} testid="rail-events-more" />}
        >
            {visible === null ? <CardSkeleton /> : visible.length === 0 ? (
                <p className="text-[11px] font-mono text-slate-500">// nothing scheduled</p>
            ) : (
                <ul className="space-y-2">
                    {visible.map((e) => (
                        <li key={e.event_id}>
                            <Link to="/events" className="block text-[12px] text-slate-200 hover:text-cyan-300 truncate" data-testid={`rail-event-${e.event_id}`}>
                                {e.title}
                            </Link>
                            <div className="text-[10px] font-mono text-slate-500">
                                {e.start ? <span className="text-cyan-300/80">{fmt(e.start)}</span> : null}
                                {e.format ? <> · {e.format}</> : null}
                                {e.organizer ? <> · <span className="truncate">{e.organizer}</span></> : null}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </CardShell>
    );
}


function TrendingTagsCard({ ctx }) {
    const [tags, setTags] = useState(null);
    const [shown, setShown] = useState(8);
    useEffect(() => {
        api.get("/tags?sort=trending&limit=30")
            .then(({ data }) => setTags(Array.isArray(data) ? data : (data?.items || [])))
            .catch(() => setTags([]));
    }, []);
    const visible = tags ? tags.slice(0, shown) : null;
    const hasMore = tags ? shown < tags.length : false;
    return (
        <CardShell id="tags" icon={TrendUp} title="Trending tags" kicker="hottest tags this week" testid="rail-tags" ctx={ctx}
            footer={<LoadMoreFooter hasMore={hasMore} loadingMore={false} onClick={() => setShown((s) => s + 8)} testid="rail-tags-more" />}
        >
            {visible === null ? <CardSkeleton /> : visible.length === 0 ? (
                <p className="text-[11px] font-mono text-slate-500">// nothing trending yet</p>
            ) : (
                <ul className="flex flex-wrap gap-1.5">
                    {visible.map((t) => (
                        <li key={t.name || t}>
                            <Link
                                to={`/?tag=${t.name || t}`}
                                className="text-[11px] font-mono text-slate-300 border border-white/10 hover:border-cyan-500/60 hover:text-cyan-300 px-1.5 py-0.5 rounded-sm transition-colors"
                                data-testid={`rail-tag-${t.name || t}`}
                                data-notranslate
                            >
                                #{t.name || t}
                                {(t.use_count || t.count) ? <span className="text-slate-500 ml-1">{t.use_count || t.count}</span> : null}
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </CardShell>
    );
}


function TopHackersCard({ ctx }) {
    const { items, hasMore, loadingMore, loadMore } = useWidgetData(
        (offset, limit) => `/users/top?limit=${limit}&offset=${offset}&range=all`,
        (d) => Array.isArray(d) ? d : (d?.items || []),
    );
    return (
        <CardShell id="hackers" icon={Trophy} title="Top hackers" kicker="all-time" testid="rail-hackers" ctx={ctx}
            action={<Link to="/leaderboard" className="text-[10px] font-mono text-slate-500 hover:text-cyan-300 uppercase tracking-widest">all</Link>}
            footer={<LoadMoreFooter hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} testid="rail-hackers-more" />}
        >
            {items === null ? <CardSkeleton /> : items.length === 0 ? (
                <p className="text-[11px] font-mono text-slate-500">// no entries yet</p>
            ) : (
                <ul className="space-y-1.5">
                    {items.map((h, i) => (
                        <li key={h.user_id || h.username} className="flex items-center gap-2 text-[12px]">
                            <span className="w-5 text-center text-cyan-300 font-mono text-[11px]">{i + 1}</span>
                            <Link
                                to={`/u/${h.username}`}
                                className="text-slate-200 hover:text-cyan-300 truncate flex-1 min-w-0"
                                data-testid={`rail-hacker-${h.username}`}
                            >
                                {h.username}
                            </Link>
                            <span className="text-amber-300 font-mono text-[10px]">{(h.karma || h.score || 0).toLocaleString()}</span>
                        </li>
                    ))}
                </ul>
            )}
        </CardShell>
    );
}


function ActiveCtfsCard({ ctx }) {
    // CTF endpoint returns ALL active challenges — no offset. We paginate
    // client-side by expanding the visible window.
    const [all, setAll] = useState(null);
    const [shown, setShown] = useState(PAGE_SIZE);
    useEffect(() => {
        api.get("/ctf/challenges")
            .then(({ data }) => setAll(Array.isArray(data) ? data : (data?.items || [])))
            .catch(() => setAll([]));
    }, []);
    const visible = all ? all.slice(0, shown) : null;
    const hasMore = all ? shown < all.length : false;
    const diff = (d) => ({ easy: "text-emerald-300", medium: "text-amber-300", hard: "text-rose-300" }[d] || "text-slate-300");
    return (
        <CardShell id="ctfs" icon={Flag} title="Live CTFs" kicker="solve a flag" testid="rail-ctfs" ctx={ctx}
            action={<Link to="/ctf" className="text-[10px] font-mono text-slate-500 hover:text-cyan-300 uppercase tracking-widest">all</Link>}
            footer={<LoadMoreFooter hasMore={hasMore} loadingMore={false} onClick={() => setShown((s) => s + LOAD_MORE_STEP)} testid="rail-ctfs-more" />}
        >
            {visible === null ? <CardSkeleton /> : visible.length === 0 ? (
                <p className="text-[11px] font-mono text-slate-500">// no active challenges</p>
            ) : (
                <ul className="space-y-2">
                    {visible.map((c) => (
                        <li key={c.challenge_id}>
                            <Link to={`/ctf/${c.challenge_id}`} className="block text-[12px] text-slate-200 hover:text-cyan-300 truncate" data-testid={`rail-ctf-${c.challenge_id}`}>
                                {c.title}
                            </Link>
                            <div className="text-[10px] font-mono text-slate-500">
                                {c.category} · <span className={diff(c.difficulty)}>{c.difficulty}</span> · +{c.points || 100} pts
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </CardShell>
    );
}


function OpenBountiesCard({ ctx }) {
    // External directory (HackerOne/Bugcrowd/…) — page-based pagination.
    const [items, setItems] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    useEffect(() => {
        api.get(`/directory/bounties?bounties_only=true&per_page=${PAGE_SIZE}&page=1`)
            .then(({ data }) => {
                const arr = data?.items || [];
                setItems(arr);
                setHasMore(arr.length === PAGE_SIZE);
            })
            .catch(() => { setItems([]); setHasMore(false); });
    }, []);
    const loadMore = useCallback(async () => {
        if (loadingMore) return;
        setLoadingMore(true);
        try {
            const { data } = await api.get(`/directory/bounties?bounties_only=true&per_page=${PAGE_SIZE}&page=${page + 1}`);
            const arr = data?.items || [];
            setItems((prev) => [...(prev || []), ...arr]);
            setPage((p) => p + 1);
            setHasMore(arr.length === PAGE_SIZE);
        } catch { setHasMore(false); }
        setLoadingMore(false);
    }, [page, loadingMore]);
    const money = (n) => n >= 1000000 ? `$${(n / 1000000).toFixed(n % 1000000 ? 1 : 0)}M` : n >= 1000 ? `$${Math.round(n / 1000)}k` : `$${Math.round(n)}`;
    return (
        <CardShell id="bounties" icon={Bug} title="Open bounties" kicker="paying hunters now" testid="rail-bounties" ctx={ctx}
            action={<Link to="/programs" className="text-[10px] font-mono text-slate-500 hover:text-cyan-300 uppercase tracking-widest">all</Link>}
            footer={<LoadMoreFooter hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} testid="rail-bounties-more" />}
        >
            {items === null ? <CardSkeleton /> : items.length === 0 ? (
                <p className="text-[11px] font-mono text-slate-500">// no programs listed</p>
            ) : (
                <ul className="space-y-2">
                    {items.map((b) => (
                        <li key={b.slug || b.name}>
                            <a
                                href={b.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-[12px] text-slate-200 hover:text-cyan-300 truncate"
                                data-testid={`rail-bounty-${b.slug || b.name}`}
                            >
                                {b.name}
                            </a>
                            <div className="text-[10px] font-mono text-slate-500">
                                {b.platform || "other"}
                                {b.max_bounty ? <> · <span className="text-emerald-300">up to {money(b.max_bounty)}</span></> : null}
                                {b.scope_count ? <> · {b.scope_count} scopes</> : null}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </CardShell>
    );
}


function ShortcutCard({ ctx }) {
    return (
        <CardShell id="shortcuts" icon={Sparkle} title="Quick start" kicker="popular destinations" testid="rail-shortcuts" ctx={ctx}>
            <ul className="space-y-1">
                {[
                    ["/ask", "Ask the community"],
                    ["/labs", "Solve a vulnerable lab"],
                    ["/quests", "Today's quests"],
                    ["/jobs", "Browse infosec jobs"],
                    ["/programs", "Bug bounty directory"],
                ].map(([href, label]) => (
                    <li key={href}>
                        <Link to={href} className="block text-[12px] font-mono text-slate-300 hover:text-cyan-300 px-1.5 py-0.5 rounded-sm hover:bg-slate-800/40 transition-colors">
                            ↳ {label}
                        </Link>
                    </li>
                ))}
            </ul>
        </CardShell>
    );
}


// Re-exported in case a future page wants a single widget.
export { TrendingTagsCard, TopHackersCard, ActiveCtfsCard, OpenBountiesCard, Eye };
